import type {Icon} from '@primer/octicons-react'
import {ProgressBar, type ProgressBarProps, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import {createContext, type MouseEvent, type PropsWithChildren, type ReactNode, use, useId} from 'react'
import styles from './MiniMergeSections.module.css'
import {MiniStatusIcon, type MiniStatusIconProps} from './MiniStatusIcon'
import {Link, type To} from '@github-ui/react-router'

type MiniMergeStatusItemProps = PropsWithChildren<{
  'aria-label': string
  'aria-expanded'?: boolean
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void
  to?: To
}>

const MiniStatusLabelIdContext = createContext<string | undefined>(undefined)

function MiniMergeStatusItem({
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  children,
  onClick,
  to,
}: MiniMergeStatusItemProps) {
  const labelId = useId()
  const actionId = useId()

  if (!onClick && !to) {
    return (
      <MiniStatusLabelIdContext value={labelId}>
        <section aria-label={ariaLabel} className={styles.MiniStatus}>
          {children}
        </section>
      </MiniStatusLabelIdContext>
    )
  }

  return (
    <MiniStatusLabelIdContext value={labelId}>
      <div className={clsx(styles.MiniStatus, styles.MiniStatusInteractive, ariaExpanded && styles.MiniStatusExpanded)}>
        {to ? (
          <Link
            to={to}
            aria-labelledby={`${labelId} ${actionId}`}
            aria-expanded={ariaExpanded}
            className={styles.MiniStatusButton}
          >
            <VisuallyHidden id={actionId}>{ariaLabel}</VisuallyHidden>
          </Link>
        ) : (
          <button
            aria-labelledby={`${labelId} ${actionId}`}
            aria-expanded={ariaExpanded}
            className={styles.MiniStatusButton}
            onClick={onClick}
            type="button"
          >
            <VisuallyHidden id={actionId}>{ariaLabel}</VisuallyHidden>
          </button>
        )}
        {children}
      </div>
    </MiniStatusLabelIdContext>
  )
}

function LabelRow({children, icon: IconComponent}: {children: ReactNode; icon?: Icon}) {
  return (
    <div className={styles.MiniStatusLabelRow}>
      {children}
      {IconComponent ? <IconComponent aria-hidden="true" /> : null}
    </div>
  )
}
type LabelProps = PropsWithChildren<{
  icon?: MiniStatusIconProps['type']
  isUnavailable?: boolean
  successful?: number
  status?: string
  total?: number
}>

function Label({icon, isUnavailable = false, successful, status, total, children}: LabelProps) {
  const labelId = use(MiniStatusLabelIdContext)
  const hasRatio = successful !== undefined && total !== undefined
  const iconType = isUnavailable ? 'empty' : icon
  const ratioText = hasRatio ? (isUnavailable || total === 0 ? '0' : `${successful}/${total}`) : undefined

  return (
    <span id={labelId} className={clsx('d-flex flex-items-center gap-1', styles.MiniStatusLabel)}>
      {iconType ? <MiniStatusIcon type={iconType} /> : null}
      <strong className={styles.MiniStatusLabelText}>{children}</strong>
      {ratioText !== undefined ? (
        <span className={clsx('fgColor-muted', styles.MiniStatusRatio)}>{ratioText}</span>
      ) : null}
      {status ? <VisuallyHidden>{status}</VisuallyHidden> : null}
    </span>
  )
}

function Progress({
  'aria-label': ariaLabel,
  isUnavailable,
  successful,
  total,
  unavailableLabel,
}: Pick<ProgressBarProps, 'aria-label'> & {
  isUnavailable: boolean
  successful: number
  total: number
  unavailableLabel: string
}) {
  if (isUnavailable) {
    return (
      <div
        className={clsx(styles.MiniStatusProgress, styles.MiniStatusUnavailableProgress)}
        data-testid="mini-status-unavailable-progress"
      >
        <span className="sr-only">{unavailableLabel}</span>
      </div>
    )
  }

  const isEmpty = total === 0

  return (
    <ProgressBar
      aria-label={ariaLabel}
      aria-valuenow={isEmpty ? 0 : undefined}
      bg={isEmpty ? 'neutral.emphasis' : undefined}
      className={styles.MiniStatusProgress}
      progress={isEmpty ? 100 : (successful / total) * 100}
    />
  )
}

MiniMergeStatusItem.LabelRow = LabelRow
MiniMergeStatusItem.Label = Label
MiniMergeStatusItem.Progress = Progress

export {MiniMergeStatusItem}
